/**
 * @module services/home-quick-actions
 * @description Acciones rápidas del Home (`components/Home.vue`), extraídas
 *              del componente para poder testearlas sin montar el SFC
 *              (vue-jest no compila el template de Home.vue). Cada acción
 *              puede declarar `feature`: la clave de `Organization.features`
 *              que la habilita (default ON, misma semántica que
 *              `v-has-feature`). UC-REG-256: "Agendar turnos" responde a
 *              `clinic.appointments`, igual que la entrada "Agenda" del
 *              sidebar, así un tenant con agenda externa (Campana) la apaga
 *              desde la base sin tocar código.
 */
import { isFeatureEnabled } from "@/directives/has-feature";

/**
 * @description Filtra las acciones cuyo `feature` está apagado para la org.
 * @param {Array<{feature?: string}>} actions
 * @returns {Array}
 */
function onlyEnabled(actions) {
  return actions.filter((a) => !a.feature || isFeatureEnabled(a.feature));
}

/**
 * @description Acciones rápidas de médico / secretaria / asistente.
 * @param {Object} params
 * @param {boolean} params.isSecretary - La secretaria no atiende pacientes
 * @returns {Array<{icon:string,title:string,subtitle:string,route:string}>}
 */
export function getQuickActions({ isSecretary }) {
  const actions = [];

  actions.push({
    icon: "el-icon-date",
    title: "Agendar turnos",
    subtitle: "Gestionar agenda",
    route: "appointment-calendar",
    feature: "clinic.appointments",
  });

  // UC-REG-261: misma clave que la entrada "Atenciones" del sidebar.
  if (!isSecretary) {
    actions.push({
      icon: "el-icon-first-aid-kit",
      title: "Atender pacientes",
      subtitle: "Sala de espera del día",
      route: "appointment-schedule",
      feature: "clinic.attentions",
    });
  }

  // UC-REG-261: misma clave que la entrada "Pacientes" del sidebar.
  actions.push({
    icon: "el-icon-document",
    title: "Revisar historias clínicas",
    subtitle: "Ver y editar HC de pacientes",
    route: "patient",
    feature: "clinic.patients",
  });

  // UC-REG-249: oculta la acción si el validador está apagado en la org
  // (default-ON: sin override, se muestra como siempre).
  actions.push({
    icon: "el-icon-circle-check",
    title: "Validar atenciones",
    subtitle: "Validar prestaciones",
    route: "validation",
    feature: "ooss.validator",
  });

  return onlyEnabled(actions);
}

/**
 * @description Acciones rápidas del fallback admin (dashboard OOSS apagado,
 *              UC-REG-249). Solo módulos que un admin puede abrir.
 * @returns {Array<{icon:string,title:string,subtitle:string,route:string}>}
 */
export function getAdminQuickActions() {
  return onlyEnabled([
    {
      icon: "el-icon-date",
      title: "Agendar turnos",
      subtitle: "Gestionar agenda",
      route: "appointment-calendar",
      feature: "clinic.appointments",
    },
    {
      icon: "el-icon-document",
      title: "Pacientes",
      subtitle: "Ver y editar historias clínicas",
      route: "patient",
      // UC-REG-261: misma clave que la entrada "Pacientes" del sidebar.
      feature: "clinic.patients",
    },
    {
      icon: "el-icon-folder-checked",
      title: "Gestiones de trámites",
      subtitle: "Bandeja de trámites recibidos",
      route: "procedures",
      // UC-REG-285: misma clave que "Gestiones/Tipos de trámite" del sidebar
      // (default ON; Farmalink la apaga).
      feature: "clinic.procedures",
    },
  ]);
}
